import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { getAudienceRequestRoute, ROUTES } from '@/lib/constants';
import { showNotification } from '@/services/notification';
import { useAudienceStore } from '@/stores/audience-store';
import { useAudienceBuilderUiStore } from '@/stores/audience-builder-ui-store';
import { hasRecoverableContent, hydrateStoreFrom } from '../lib/draft-store-sync';
import { reconcileDraft } from '../lib/draft-reconciler';
import { getDraft } from '../services/audience-draft-api';
import type { AudienceDraft, DraftPatch } from '../types/draft';
import type { LocalDraftMirror } from './use-local-draft-mirror';

export interface UseDraftBootResult {
  showRecoveryBanner: boolean;
  setShowRecoveryBanner: (v: boolean) => void;
  getPendingLocal: () => DraftPatch | null;
  setPendingLocal: (p: DraftPatch | null) => void;
  isHydrating: () => boolean;
  getLastSavedPatch: () => DraftPatch | null;
  setLastSavedPatch: (p: DraftPatch | null) => void;
  hydrate: (src: AudienceDraft | DraftPatch) => void;
  /** True while the initial getDraft fetch is in flight for an existing
   * draft URL. False for new-draft (no id) flow and once the fetch resolves
   * (success or failure). Drives the Target page's loading skeleton. */
  isBooting: boolean;
}

/**
 * Boots the draft session for a given URL identity. Handles:
 *   - Hard-resetting refs + store on identity transitions.
 *   - Loading the server draft (or scheduling new-draft recovery prompt).
 *   - Reconciling local mirror content and deciding when to prompt.
 *   - Hydrating the store with server or local content.
 */
export function useDraftBoot(
  idFromUrl: string | undefined,
  mirror: LocalDraftMirror,
  campaignIdFromUrl?: string,
): UseDraftBootResult {
  const navigate = useNavigate();

  const [showRecoveryBanner, setShowRecoveryBanner] = useState(false);
  const [isBooting, setIsBooting] = useState<boolean>(idFromUrl !== undefined);
  const pendingLocalRef = useRef<DraftPatch | null>(null);
  const hasBootedRef = useRef(false);
  const bootedForIdRef = useRef<string | null | undefined>(undefined);
  const isHydratingRef = useRef(false);
  const lastSavedPatchRef = useRef<DraftPatch | null>(null);

  const getPendingLocal = useCallback((): DraftPatch | null => pendingLocalRef.current, []);
  const setPendingLocal = useCallback((p: DraftPatch | null): void => {
    pendingLocalRef.current = p;
  }, []);
  const isHydrating = useCallback((): boolean => isHydratingRef.current, []);
  const getLastSavedPatch = useCallback((): DraftPatch | null => lastSavedPatchRef.current, []);
  const setLastSavedPatch = useCallback((p: DraftPatch | null): void => {
    lastSavedPatchRef.current = p;
  }, []);

  const hydrate = useCallback((src: AudienceDraft | DraftPatch): void => {
    isHydratingRef.current = true;
    try {
      hydrateStoreFrom(src);
    } finally {
      // Hydration setters flip hasUserEdited=true as a side effect. Clear
      // it so post-hydration programmatic store mutations (e.g. geo-hierarchy
      // sync when region/country queries resolve) don't trip the subscriber
      // and write a phantom mirror entry that would trigger the recovery
      // banner on next open.
      useAudienceBuilderUiStore.getState().clearUserEdited();
      queueMicrotask(() => {
        isHydratingRef.current = false;
      });
    }
  }, []);

  useEffect(() => {
    const targetId = idFromUrl ?? null;
    const storeState = useAudienceStore.getState();
    const storeDraftId = storeState.draftId;

    // NPJB-358: ?campaignId in the URL signals "start a new audience in this
    // campaign". When present without an :id, the user explicitly navigated
    // here (via "Add audience" button), so we must ALWAYS start fresh — even
    // if the store still holds a draftId from a previous session in the same
    // campaign. Without this, clicking "Add audience" twice would reopen the
    // first draft instead of creating a second one.
    const isNewAudienceEntry = campaignIdFromUrl !== undefined && targetId === null;

    // Skip cases (both disabled when the user explicitly entered for a new audience):
    // - URL has an id matching the store: store already loaded; nothing to do.
    // - URL has no id and store has a draft: continuing existing session
    //   (e.g. navigating between wizard steps within the same session).
    if (targetId !== null && targetId === storeDraftId && !isNewAudienceEntry) {
      bootedForIdRef.current = targetId;
      hasBootedRef.current = true;
      setIsBooting(false);
      return;
    }
    if (
      targetId === null &&
      (storeDraftId !== null || storeState.prefilledFromExplore) &&
      !isNewAudienceEntry
    ) {
      // Continuing an existing session, OR the store was just pre-filled from an
      // Explore→Target handoff (NPJB-308). In both cases don't reset/boot — that would
      // wipe the carried-over filters + the pre-fill banner. No backend request here.
      bootedForIdRef.current = null;
      hasBootedRef.current = true;
      setIsBooting(false);
      return;
    }

    // Identity transition (or genuine first boot for this URL, or new-audience
    // entry): hard-reset and fall through to the existing boot/fetch flow.
    storeState.reset();
    // NPJB-358: Carry the new campaignId into the fresh session so the
    // createDraft call assigns the audience to the correct campaign.
    if (campaignIdFromUrl) {
      useAudienceStore.getState().setCampaignId(campaignIdFromUrl);
    }
    // When starting a genuinely new audience, purge the shared "new" local
    // mirror so a previous session's data doesn't trigger the recovery
    // banner or leak into the autosave subscriber.
    if (isNewAudienceEntry) {
      mirror.clear();
    }
    // Full reset (not just clearUserEdited) so the store subscriber doesn't
    // immediately re-dirty with stale state from the previous session, AND
    // so nothing from the previous audience survives the switch. Without
    // this, `lastSubmittedBrief` in particular leaked across audiences
    // (fix round 2, NPJB-369): a fresh audience kept the old one's "✓
    // Applied" badge/snippet, and Cancel would inject the previous
    // audience's brief text into the new one. `reset()` clears `unmapped`,
    // `lastSubmittedBrief`, and `hasUserEdited` together; it deliberately
    // leaves `geoHierarchy`/`industryHierarchy` alone — those are populated
    // straight from react-query lookup caches (`audience-summary.tsx`,
    // `filter-groups.tsx`), not per-audience state, so they're safe (and
    // desirable) to keep. Safe to run before `boot()` below: for an existing
    // draft, `hydrateStoreFrom` (called later, async, via `hydrate()`) never
    // writes `lastSubmittedBrief` or `unmapped` — those only ever come from
    // a brief-parse response — so nothing set here is clobbered afterward.
    useAudienceBuilderUiStore.getState().reset();
    pendingLocalRef.current = null;
    lastSavedPatchRef.current = null;
    setShowRecoveryBanner(false);

    hasBootedRef.current = true;
    bootedForIdRef.current = targetId;
    setIsBooting(idFromUrl !== undefined);

    async function boot(): Promise<void> {
      if (idFromUrl) {
        try {
          const server = await getDraft(idFromUrl);
          if (server.status !== 'draft') {
            navigate(getAudienceRequestRoute(server.status, server.id), { replace: true });
            return;
          }
          const localMeta = mirror.read();
          const decision = reconcileDraft({
            local: localMeta
              ? {
                  lastEditedAt: localMeta.lastEditedAt,
                  schemaVersion: localMeta.schemaVersion,
                  draft: localMeta.draft,
                }
              : null,
            server,
          });
          if (decision.action === 'prompt-user' && localMeta) {
            pendingLocalRef.current = localMeta.draft;
            setShowRecoveryBanner(true);
          } else {
            // No meaningful divergence: purge stale local mirror so it
            // can't re-trigger prompts on subsequent opens.
            mirror.clear();
          }
          useAudienceStore.getState().setDraftIdentity({
            draftId: server.id,
            lastSeenUpdatedAt: server.updatedAt,
            status: server.status,
            following: server.following,
          });
          // NPJB-358: Set campaignId from server draft so the breadcrumb
          // renders when editing an existing draft (no ?campaignId in URL).
          if (server.campaignId) {
            useAudienceStore.getState().setCampaignId(server.campaignId);
          }
          hydrate(server);
        } catch {
          showNotification({
            message: "Couldn't load the draft. Please try again.",
            type: 'error',
          });
          navigate(ROUTES.campaigns, { replace: true });
        } finally {
          setIsBooting(false);
        }
      } else {
        // Fresh /audience-builder (no id). Only prompt if the local "new"
        // mirror has genuinely recoverable content from a prior abandoned
        // session.
        const localMeta = mirror.read();
        if (localMeta && hasRecoverableContent(localMeta.draft)) {
          pendingLocalRef.current = localMeta.draft;
          setShowRecoveryBanner(true);
        } else if (localMeta) {
          mirror.clear();
        }
      }
    }
    void boot();
  }, [idFromUrl, campaignIdFromUrl, mirror, navigate, hydrate]);

  return {
    showRecoveryBanner,
    setShowRecoveryBanner,
    getPendingLocal,
    setPendingLocal,
    isHydrating,
    getLastSavedPatch,
    setLastSavedPatch,
    hydrate,
    isBooting,
  };
}
